/*
  Norse Quiz — results-page personalisation.

  Implements the Scars Quiz "Results Page Logic": the empathy line comes from the
  skin-story question, the headline and chips from the scar-type question, and the
  ingredient bullets are built from the scar types with de-duplication, selection
  order and a cap.

  None of that mapping lives here — it arrives as JSON from the banner section's
  blocks, so marketing can retune a sentence or swap a bullet in the Theme Editor
  without a deploy. This file only knows how to apply the shape.

  The markup is already correct before this runs (the section renders the fallback
  copy server-side), so a visitor who never took the quiz, or one whose session has
  expired, reads clean complete copy rather than empty slots.
*/
(function () {
  'use strict';

  function readConfig() {
    var node = document.querySelector('[data-nqr-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('norse-quiz-results: bad config', error);
      return null;
    }
  }

  function answersFor(state, key) {
    var value = state && state.answers ? state.answers[key] : null;
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  function labelsFor(state, key) {
    var value = state && state.labels ? state.labels[key] : null;
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  /** `question` or `question, other` — a single id behaves exactly as before. */
  function keysOf(setting) {
    return String(setting || '')
      .split(',')
      .map(function (key) {
        return key.trim();
      })
      .filter(Boolean);
  }

  /**
   * Answers across several questions, in the order the questions are named and then
   * the order they were picked, de-duplicated. The concern quiz builds its bullets
   * from the main concern first and the "anything else" answers after it, and one
   * answer can appear in both.
   */
  function answersAcross(state, setting, reader) {
    var read = reader || answersFor;
    var out = [];

    keysOf(setting).forEach(function (key) {
      read(state, key).forEach(function (value) {
        if (out.indexOf(value) === -1) out.push(value);
      });
    });

    return out;
  }

  /**
   * The possessive this shopper's answers select — "your" when they are buying for
   * themselves, "their" otherwise. Falls back to the first word in the map so a
   * visitor who never took the quiz still reads a whole sentence.
   */
  function voiceWord(config) {
    var voice = config.voice;
    if (!voice || !voice.words) return '';
    var keys = Object.keys(voice.words);
    if (keys.length === 0) return '';
    return voice.words[keys[0]];
  }

  function voiceFor(config, state) {
    var voice = config.voice;
    if (!voice || !voice.words || !voice.question) return voiceWord(config);
    var chosen = answersFor(state, voice.question)[0];
    var word = chosen ? voice.words[chosen] : null;
    return word || voiceWord(config);
  }

  /** Swaps every `[voice]` token in a line for the word the answers select. */
  function applyVoice(text, config, state) {
    if (!text || text.indexOf('[voice]') === -1) return text;
    return text.split('[voice]').join(voiceFor(config, state));
  }

  /** The mapped sentence, optionally prefixed when another answer says it hurts. */
  function buildEmpathy(config, state) {
    var chosen = answersFor(state, config.empathyQuestion)[0];
    var line = (config.empathy && config.empathy[chosen]) || config.empathyFallback || '';
    if (!line) return '';

    var intensifier = config.intensifier;
    if (intensifier && intensifier.text && intensifier.question) {
      var given = answersFor(state, intensifier.question);
      var hit = (intensifier.values || []).some(function (value) {
        return given.indexOf(value) !== -1;
      });
      if (hit) return intensifier.text + ' ' + line;
    }

    return line;
  }

  function buildHeadline(config, state) {
    var chosen = answersAcross(state, config.typesQuestion);
    if (chosen.length !== 1) return config.headlineMulti || '';

    var label = answersAcross(state, config.typesQuestion, labelsFor)[0] || '';
    var template = config.headlineSingle || config.headlineMulti || '';
    if (!label) return config.headlineMulti || template;
    return template.replace('[type]', label);
  }

  /**
   * "Built for skin in its 20s." Blank when none of the questions named in
   * `age_question` answered with something the Age line blocks map.
   *
   * Naming more than one question, tried in the order given, is how a quiz that skips
   * its age question for some shoppers still gets a line: the concern quiz does not ask
   * the age of a teen the buyer is shopping for, so it names `age, buyer` and carries an
   * Age line block matching `teen`. An unmapped answer falls through to the next
   * question rather than blanking the line, so the fallback works whichever way the
   * shopper went.
   */
  function buildAgeLine(config, state) {
    if (!config.ageQuestion || !config.age) return '';

    var keys = keysOf(config.ageQuestion);
    for (var i = 0; i < keys.length; i++) {
      var chosen = answersFor(state, keys[i])[0];
      if (chosen && config.age[chosen]) return config.age[chosen];
    }

    return '';
  }

  /**
   * One bullet per matching rule, in the order the shopper picked the types, with
   * shared rules collapsing to a single line (ice pick + boxcar are one bullet).
   * The always-on rules are appended last, and the cap is applied at the end so a
   * long selection never pushes them off.
   */
  function buildBullets(config, state) {
    var chosen = answersAcross(state, config.typesQuestion);
    var rules = config.rules || [];
    var always = [];
    var matched = [];
    var used = [];

    rules.forEach(function (rule) {
      if (!rule.match || rule.match.length === 0) always.push(rule.text);
    });

    var cap = parseInt(config.maxBullets, 10);

    // The cap applies to the un-answered state too, so the box keeps the same
    // number of rows as the design whether or not the quiz was taken.
    if (chosen.length === 0) {
      var fallback = config.fallbackBullets || [];
      if (cap > 0) fallback = fallback.slice(0, Math.max(0, cap - always.length));
      return fallback.concat(always);
    }

    chosen.forEach(function (value) {
      rules.forEach(function (rule, index) {
        if (!rule.match || rule.match.length === 0) return;
        if (used.indexOf(index) !== -1) return;
        if (rule.match.indexOf(value) === -1) return;
        used.push(index);
        matched.push(rule.text);
      });
    });

    if (cap > 0 && matched.length + always.length > cap) {
      matched = matched.slice(0, Math.max(0, cap - always.length));
    }

    return matched.concat(always);
  }

  function paintList(target, items, className, iconMarkup) {
    if (!target) return;
    target.textContent = '';

    items.forEach(function (text) {
      if (!text) return;
      var li = document.createElement('li');
      li.className = className;
      if (iconMarkup) {
        var icon = document.createElement('span');
        icon.className = 'nqr-ingredients__icon';
        icon.innerHTML = iconMarkup;
        li.appendChild(icon);
      }
      li.appendChild(document.createTextNode(text));
      target.appendChild(li);
    });
  }

  function apply() {
    var config = readConfig();
    if (!config) return;

    var state = window.NorseQuiz ? window.NorseQuiz.read() : null;
    if (!state || !state.answers) return;

    /*
      The whole greeting, when dropping the name should drop its punctuation with it:
      "Your results are in", not "Great news, your results are in". The name token is
      inside this element, so replacing the text destroys it — which is fine, because
      this only runs when there is no name to put in it.
    */
    var greeting = document.querySelector('.nqr-banner__greeting');
    var firstName = state.tokens ? state.tokens.first_name : '';
    if (greeting && config.greetingFallbackLine && !firstName) {
      greeting.textContent = applyVoice(config.greetingFallbackLine, config, state);
    }

    var kicker = document.querySelector('[data-nqr-kicker]');
    if (kicker) {
      var kickerText = applyVoice(kicker.dataset.nqrTemplate || kicker.textContent, config, state);
      // Kept so a re-apply after the quiz completes substitutes into the original
      // string rather than into one that already has a word in the slot.
      if (!kicker.dataset.nqrTemplate) kicker.dataset.nqrTemplate = kicker.textContent;
      kicker.textContent = kickerText;
    }

    var empathy = document.querySelector('[data-nqr-empathy]');
    if (empathy) {
      var line = applyVoice(buildEmpathy(config, state), config, state);
      empathy.textContent = line;
      empathy.hidden = !line;
    }

    /*
      Every one, for the same reason as the bullets below: the age line lives inside
      the ingredients panel now, and a page that recommends one of several bundles
      ships that panel once per buy box.
    */
    var ageLine = applyVoice(buildAgeLine(config, state), config, state);
    Array.prototype.forEach.call(document.querySelectorAll('[data-nqr-age]'), function (age) {
      age.textContent = ageLine;
      age.hidden = !ageLine;
    });

    var headline = document.querySelector('[data-nqr-headline]');
    if (headline) {
      var text = applyVoice(buildHeadline(config, state), config, state);
      if (text) headline.textContent = text;
    }

    var chips = document.querySelector('[data-nqr-chips]');
    if (chips) {
      var labels = answersAcross(state, config.typesQuestion, labelsFor);
      paintList(chips, labels, 'nqr-banner__chip', '');
      chips.hidden = labels.length === 0;
    }

    /*
      Every list, not the first. A page that recommends one of several bundles ships a
      buy box per route and shows one — taking the first left the visible box on its
      Liquid fallback bullets whenever the route was not the first box on the page.
    */
    var items = buildBullets(config, state);
    Array.prototype.forEach.call(document.querySelectorAll('[data-nqr-bullets]'), function (bullets) {
      // Reuse the icon the section already rendered so the SVG stays in Liquid.
      var existing = bullets.querySelector('.nqr-ingredients__icon');
      var icon = existing ? existing.innerHTML : '';
      paintList(bullets, items, 'nqr-ingredients__item', icon);
    });
  }

  // Exposed so the mapping can be exercised without a page, and so another
  // results block can reuse the same rules instead of reimplementing them.
  window.NorseQuizResults = {
    buildEmpathy: buildEmpathy,
    buildHeadline: buildHeadline,
    buildBullets: buildBullets,
    buildAgeLine: buildAgeLine,
    voiceFor: voiceFor,
    applyVoice: applyVoice,
    apply: apply,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }

  document.addEventListener('norse:quiz-complete', apply);
  document.addEventListener('shopify:section:load', apply);
})();
